import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, CircularProgress, TextField, IconButton,
  Divider, Avatar, Badge, Button, Dialog, InputAdornment,
  DialogTitle, DialogContent, DialogActions, List, ListItemButton,
  ListItemText, ListItemAvatar, Stack, Tooltip, Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { helpService, HelpConversation, HelpMessage } from '../common/services/helpService';
import { Facilitator } from './facilitator';

const POLL_INTERVAL_MS = 4000;

const STATUS_COLOR: Record<number, 'default' | 'warning' | 'success' | 'info' | 'error'> = {
  0: 'default', 1: 'warning', 2: 'success', 3: 'info', 4: 'default',
};
const STATUS_BG: Record<number, string> = {
  0: '#eeeeee', 1: '#fff3e0', 2: '#e8f5e9', 3: '#e1f5fe', 4: '#f5f5f5',
};

// Quick reply templates
const QUICK_REPLIES = [
  'Hello! We have received your query and will assist you shortly.',
  'Could you please provide more details about your concern?',
  'We are looking into this and will get back to you soon.',
  'Your issue has been noted. Our team will call you at the number provided.',
  'Thank you for contacting eAbhyas support!',
];

const senderBubbleBg = (type: number) => {
  if (type === 2) return { bg: '#1976d2', color: '#fff' };   // admin → blue
  if (type === 1) return { bg: '#e8eaf6', color: '#1a237e' }; // bot → indigo-tint
  return { bg: '#f5f5f5', color: '#212121' };                  // user → grey
};

const SenderAvatar: React.FC<{ type: number; sm?: boolean }> = ({ type, sm }) => {
  const sz = sm ? 26 : 32;
  if (type === 1) return <Avatar sx={{ width: sz, height: sz, bgcolor: '#5c6bc0' }}><SmartToyIcon sx={{ fontSize: sz * 0.55 }} /></Avatar>;
  if (type === 2) return <Avatar sx={{ width: sz, height: sz, bgcolor: '#2e7d32' }}><SupportAgentIcon sx={{ fontSize: sz * 0.55 }} /></Avatar>;
  return <Avatar sx={{ width: sz, height: sz, bgcolor: '#ef6c00' }}><PersonIcon sx={{ fontSize: sz * 0.55 }} /></Avatar>;
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const formatDate = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const timeAgo = (iso: string) => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const isSameDay = (a: string, b: string) =>
  new Date(a).toDateString() === new Date(b).toDateString();

// Filter options
type FilterKey = 'all' | 'waiting' | 'active' | 'callback' | 'resolved';
const FILTERS: { key: FilterKey; label: string; status?: number }[] = [
  { key: 'all', label: 'All' },
  { key: 'waiting', label: 'Waiting', status: 1 },
  { key: 'active', label: 'Active', status: 2 },
  { key: 'callback', label: 'Callback', status: 3 },
  { key: 'resolved', label: 'Resolved', status: 4 },
];

export const AdminHelpDesk: React.FC = () => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState<HelpConversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<HelpConversation | null>(null);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const lastSentAtRef = useRef<string | undefined>(undefined);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentStatus = FILTERS.find(f => f.key === filter)?.status;

  const scrollBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, []);

  const loadConversations = useCallback(async (silent = false) => {
    if (!silent) setLoadingConvs(true);
    else setRefreshing(true);
    try {
      const list = await helpService.getConversations(currentStatus);
      setConversations(list);
    } catch {}
    finally { setLoadingConvs(false); setRefreshing(false); }
  }, [currentStatus]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    const t = setInterval(() => loadConversations(true), 10000);
    return () => clearInterval(t);
  }, [loadConversations]);

  const openConversation = async (conv: HelpConversation) => {
    if (selectedConv?.conversationId === conv.conversationId) return;
    setSelectedConv(conv);
    setMessages([]);
    setInputText('');
    setShowInfo(false);
    lastSentAtRef.current = undefined;
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setLoadingMsgs(true);
    try {
      const full = await helpService.getConversation(conv.conversationId);
      if (full) {
        setMessages(full.messages);
        setSelectedConv(full);
        if (full.messages.length > 0)
          lastSentAtRef.current = full.messages[full.messages.length - 1].sentAt;
        scrollBottom();
      }
    } catch {} finally { setLoadingMsgs(false); }
  };

  const poll = useCallback(async () => {
    if (!selectedConv || selectedConv.status === 4) return;
    try {
      const newMsgs = await helpService.getMessages(selectedConv.conversationId, lastSentAtRef.current);
      if (newMsgs.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.messageId));
          const fresh = newMsgs.filter(m => !existingIds.has(m.messageId));
          if (fresh.length === 0) return prev;
          lastSentAtRef.current = fresh[fresh.length - 1].sentAt;
          return [...prev, ...fresh];
        });
        scrollBottom();
      }
    } catch {}
  }, [selectedConv, scrollBottom]);

  useEffect(() => {
    if (!selectedConv || selectedConv.status === 4) return;
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [selectedConv, poll]);

  const markLocalActive = (convId: number) => {
    setSelectedConv(prev => prev ? { ...prev, status: 2, statusLabel: 'Active', acknowledgedAt: new Date().toISOString() } : prev);
    setConversations(prev => prev.map(c => c.conversationId === convId ? { ...c, status: 2, statusLabel: 'Active' } : c));
  };

  const sendMessage = async (text?: string) => {
    const msg_text = (text ?? inputText).trim();
    if (!msg_text || !selectedConv || sending) return;
    setInputText('');
    setSending(true);
    try {
      const msg = await helpService.sendMessage(selectedConv.conversationId, msg_text);
      if (msg) {
        setMessages(prev => [...prev, msg]);
        lastSentAtRef.current = msg.sentAt;
        if (!selectedConv.acknowledgedAt) markLocalActive(selectedConv.conversationId);
        scrollBottom();
      }
    } catch {} finally { setSending(false); }
  };

  const acknowledge = async () => {
    if (!selectedConv || acknowledging) return;
    setAcknowledging(true);
    try {
      const msg = await helpService.sendMessage(selectedConv.conversationId, QUICK_REPLIES[0]);
      if (msg) {
        setMessages(prev => [...prev, msg]);
        lastSentAtRef.current = msg.sentAt;
        markLocalActive(selectedConv.conversationId);
        scrollBottom();
      }
    } catch {} finally { setAcknowledging(false); }
  };

  const submitResolve = async () => {
    if (!selectedConv) return;
    setResolving(true);
    try {
      await helpService.resolveConversation(selectedConv.conversationId, resolveNote || undefined);
      setSelectedConv(prev => prev ? { ...prev, status: 4, statusLabel: 'Resolved' } : prev);
      setConversations(prev => prev.map(c =>
        c.conversationId === selectedConv.conversationId ? { ...c, status: 4, statusLabel: 'Resolved' } : c));
      setResolveOpen(false);
      setResolveNote('');
    } catch {} finally { setResolving(false); }
  };

  // Filtered + searched list
  const visibleConvs = conversations.filter(c =>
    !search || c.schoolName.toLowerCase().includes(search.toLowerCase()));

  const countOf = (status: number) => conversations.filter(c => c.status === status).length;
  const waitingCount = countOf(1);
  const callbackCount = countOf(3);

  return (
    <Facilitator>
      <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', bgcolor: '#f5f5f5' }}>

        {/* ══ LEFT PANEL ══════════════════════════════════════════ */}
        <Box sx={{
          width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column',
          bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <HeadsetMicIcon color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight={700} sx={{ flex: 1, fontSize: 16 }}>Help Desk</Typography>
              <Tooltip title="Refresh list">
                <IconButton size="small" onClick={() => loadConversations(true)} disabled={refreshing}>
                  {refreshing
                    ? <CircularProgress size={14} />
                    : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* Stats row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
              {[
                { label: 'Waiting', count: waitingCount, color: '#f57c00', bg: '#fff3e0' },
                { label: 'Active', count: countOf(2), color: '#2e7d32', bg: '#e8f5e9' },
                { label: 'Callback', count: callbackCount, color: '#0277bd', bg: '#e1f5fe' },
                { label: 'Resolved', count: countOf(4), color: '#616161', bg: '#f5f5f5' },
              ].map(s => (
                <Box key={s.label} sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: s.bg, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                  onClick={() => setFilter(FILTERS.find(f => f.label === s.label)?.key ?? 'all')}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: s.color, fontSize: 13 }}>{s.count}</Typography>
                  <Typography variant="caption" sx={{ color: s.color, fontSize: 11 }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Search */}
            <TextField
              fullWidth size="small" placeholder="Search school…"
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
            />
          </Box>

          {/* Filter chips */}
          <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <Chip
                key={f.key}
                label={
                  f.key === 'waiting' ? <Badge badgeContent={waitingCount || undefined} color="warning" sx={{ '& .MuiBadge-badge': { right: -10, top: 0 } }}>{f.label}</Badge>
                  : f.key === 'callback' ? <Badge badgeContent={callbackCount || undefined} color="info" sx={{ '& .MuiBadge-badge': { right: -10, top: 0 } }}>{f.label}</Badge>
                  : f.label
                }
                size="small"
                variant={filter === f.key ? 'filled' : 'outlined'}
                color={filter === f.key ? 'primary' : 'default'}
                onClick={() => setFilter(f.key)}
                sx={{ fontSize: 11, height: 24, cursor: 'pointer' }}
              />
            ))}
          </Box>

          <Divider />

          {/* Conversation list */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loadingConvs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
                <CircularProgress size={24} />
              </Box>
            ) : visibleConvs.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 6, px: 2 }}>
                <HeadsetMicIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.disabled">No conversations</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {visibleConvs.map(conv => {
                  const sel = selectedConv?.conversationId === conv.conversationId;
                  const urgent = conv.status === 1 || conv.status === 3;
                  return (
                    <React.Fragment key={conv.conversationId}>
                      <ListItemButton
                        selected={sel}
                        onClick={() => openConversation(conv)}
                        sx={{
                          py: 1.25, px: 1.5, alignItems: 'flex-start',
                          borderLeft: '3px solid',
                          borderLeftColor: conv.status === 1 ? 'warning.main'
                            : conv.status === 2 ? 'success.main'
                            : conv.status === 3 ? 'info.main' : 'transparent',
                          bgcolor: sel ? STATUS_BG[conv.status] : undefined,
                          '&:hover': { bgcolor: STATUS_BG[conv.status] },
                          '&.Mui-selected': { bgcolor: STATUS_BG[conv.status] },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 44, mt: 0.25 }}>
                          <Badge
                            variant="dot"
                            color={urgent ? 'warning' : 'default'}
                            invisible={!urgent}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          >
                            <Avatar sx={{
                              width: 36, height: 36, fontSize: 15, fontWeight: 700,
                              bgcolor: conv.status === 1 ? '#f57c00'
                                : conv.status === 2 ? '#2e7d32'
                                : conv.status === 3 ? '#0277bd' : '#9e9e9e',
                            }}>
                              {conv.status === 3
                                ? <PhoneCallbackIcon sx={{ fontSize: 18 }} />
                                : conv.schoolName[0].toUpperCase()}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          disableTypography
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 158 }}>
                                {conv.schoolName}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, flexShrink: 0 }}>
                                {timeAgo(conv.createdAt)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
                              <Chip
                                label={conv.statusLabel}
                                color={STATUS_COLOR[conv.status] ?? 'default'}
                                size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                              />
                              {conv.phone && (
                                <Typography variant="caption" color="info.main" noWrap sx={{ fontSize: 11 }}>
                                  📞 {conv.phone}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItemButton>
                      <Divider component="li" />
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        {/* ══ RIGHT PANEL ════════════════════════════════════════ */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {!selectedConv ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: '#f5f5f5' }}>
              <HeadsetMicIcon sx={{ fontSize: 72, color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary" fontWeight={600}>
                Select a conversation
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {visibleConvs.length
                  ? `${visibleConvs.length} conversation${visibleConvs.length !== 1 ? 's' : ''} in this view`
                  : 'No conversations match the current filter'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* ── Conv Header ── */}
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{
                    width: 38, height: 38, fontSize: 16, fontWeight: 700,
                    bgcolor: selectedConv.status === 1 ? '#f57c00' : selectedConv.status === 2 ? '#2e7d32' : selectedConv.status === 3 ? '#0277bd' : '#9e9e9e',
                  }}>
                    {selectedConv.schoolName[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>{selectedConv.schoolName}</Typography>
                      <Chip label={selectedConv.statusLabel} color={STATUS_COLOR[selectedConv.status]} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Started {formatDateTime(selectedConv.createdAt)}
                      {selectedConv.acknowledgedAt && ` · Acknowledged ${timeAgo(selectedConv.acknowledgedAt)}`}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexShrink={0}>
                    {selectedConv.status === 1 && (
                      <Button variant="outlined" size="small" color="warning" startIcon={<HeadsetMicIcon />}
                        onClick={acknowledge} disabled={acknowledging} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        {acknowledging ? 'Sending…' : 'Acknowledge'}
                      </Button>
                    )}
                    {selectedConv.status !== 4 && (
                      <Button variant="contained" size="small" color="success" startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => setResolveOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Resolve
                      </Button>
                    )}
                    <Tooltip title="Conversation details">
                      <IconButton size="small" onClick={() => setShowInfo(v => !v)}
                        sx={{ bgcolor: showInfo ? 'primary.50' : undefined }}>
                        <InfoOutlinedIcon fontSize="small" color={showInfo ? 'primary' : 'inherit'} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>

                {/* Banners */}
                {selectedConv.status === 3 && (
                  <Alert severity="info" icon={<PhoneCallbackIcon />} sx={{ mt: 1, py: 0, fontSize: 13 }}>
                    <strong>Callback requested</strong>
                    {selectedConv.phone && <> · {selectedConv.phone}</>}
                    {selectedConv.callbackQuery && <> · "{selectedConv.callbackQuery}"</>}
                  </Alert>
                )}
                {selectedConv.status === 4 && selectedConv.resolutionNote && (
                  <Alert severity="success" icon={<AssignmentTurnedInIcon />} sx={{ mt: 1, py: 0, fontSize: 13 }}>
                    <strong>Resolution: </strong>{selectedConv.resolutionNote}
                  </Alert>
                )}
              </Box>

              {/* ── Body: messages + optional info panel ── */}
              <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* Messages area */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                  <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: '#f5f5f5' }}>
                    {loadingMsgs ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      messages.map((msg, idx) => {
                        const isAdmin = msg.senderType === 2;
                        const showDateSep = idx === 0 || !isSameDay(messages[idx - 1].sentAt, msg.sentAt);
                        const { bg, color } = senderBubbleBg(msg.senderType);
                        return (
                          <React.Fragment key={msg.messageId}>
                            {showDateSep && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                                <Divider sx={{ flex: 1, borderColor: 'rgba(0,0,0,0.1)' }} />
                                <Chip label={formatDate(msg.sentAt)} size="small" sx={{ fontSize: 11, bgcolor: 'rgba(255,255,255,0.85)', border: '1px solid rgba(0,0,0,0.12)', backdropFilter: 'blur(4px)', fontWeight: 500 }} />
                                <Divider sx={{ flex: 1, borderColor: 'rgba(0,0,0,0.1)' }} />
                              </Box>
                            )}
                            <Box sx={{
                              display: 'flex',
                              flexDirection: isAdmin ? 'row-reverse' : 'row',
                              alignItems: 'flex-end',
                              gap: 0.75,
                            }}>
                              <SenderAvatar type={msg.senderType} sm />
                              <Box sx={{ maxWidth: '70%' }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    mb: 0.25,
                                    textAlign: isAdmin ? 'right' : 'left',
                                    fontSize: 11,
                                  }}
                                >
                                  {msg.senderName} · {formatTime(msg.sentAt)}
                                </Typography>
                                <Box sx={{
                                  px: 1.5, py: 1,
                                  bgcolor: bg, color,
                                  borderRadius: 2.5,
                                  borderBottomRightRadius: isAdmin ? 4 : 10,
                                  borderBottomLeftRadius: isAdmin ? 10 : 4,
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                }}>
                                  <Typography variant="body2" sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'inherit' }}>
                                    {msg.message}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })
                    )}
                    {sending && (
                      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                        <SenderAvatar type={2} sm />
                        <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#1976d2', borderRadius: 2.5, borderBottomRightRadius: 4 }}>
                          <Typography variant="body2" sx={{ color: 'white', fontSize: 13 }}>Sending…</Typography>
                        </Box>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Quick replies */}
                  {selectedConv.status !== 4 && (
                    <Box sx={{ px: 2, pt: 0.75, pb: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap', bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                      {QUICK_REPLIES.slice(1).map((r, i) => (
                        <Chip key={i} label={r} size="small" variant="outlined"
                          onClick={() => sendMessage(r)}
                          sx={{ fontSize: 11, height: 22, maxWidth: 280, cursor: 'pointer',
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
                          }} />
                      ))}
                    </Box>
                  )}

                  {/* Input box */}
                  {selectedConv.status !== 4 ? (
                    <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end', borderTop: '1px solid', borderColor: 'divider', boxShadow: '0 -2px 8px rgba(0,0,0,0.04)' }}>
                      <TextField
                        fullWidth size="small" placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
                        value={inputText}
                        onChange={e => setInputText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        disabled={sending}
                        multiline maxRows={4}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: 13.5, bgcolor: '#f9fafb' } }}
                      />
                      <Tooltip title="Send (Enter)">
                        <span>
                          <IconButton
                            onClick={() => sendMessage()}
                            disabled={!inputText.trim() || sending}
                            sx={{
                              width: 40, height: 40,
                              bgcolor: inputText.trim() ? 'primary.main' : 'grey.200',
                              color: inputText.trim() ? 'white' : 'text.disabled',
                              borderRadius: '50%',
                              '&:hover': { bgcolor: 'primary.dark' },
                              transition: 'all 0.2s',
                            }}
                          >
                            <SendIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Box sx={{ px: 3, py: 1.5, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        <DoneAllIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: 'success.main' }} />
                        Conversation resolved · {selectedConv.resolvedByName && `by ${selectedConv.resolvedByName}`}
                        {selectedConv.minutesToResolve !== undefined && ` in ${selectedConv.minutesToResolve}m`}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* ── Info Side Panel ── */}
                {showInfo && (
                  <Box sx={{
                    width: 240, flexShrink: 0, borderLeft: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper', overflow: 'auto', p: 2,
                  }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Conversation Info</Typography>
                    {[
                      { label: 'School', value: selectedConv.schoolName },
                      { label: 'Status', value: selectedConv.statusLabel },
                      { label: 'Started', value: formatDateTime(selectedConv.createdAt) },
                      { label: 'Phone', value: selectedConv.phone ?? '—' },
                      { label: 'Acknowledged by', value: selectedConv.acknowledgedByName ?? '—' },
                      { label: 'Acknowledged at', value: selectedConv.acknowledgedAt ? formatDateTime(selectedConv.acknowledgedAt) : '—' },
                      { label: 'Time to acknowledge', value: selectedConv.minutesToAcknowledge !== undefined ? `${selectedConv.minutesToAcknowledge} min` : '—' },
                      { label: 'Resolved by', value: selectedConv.resolvedByName ?? '—' },
                      { label: 'Resolved at', value: selectedConv.resolvedAt ? formatDateTime(selectedConv.resolvedAt) : '—' },
                      { label: 'Time to resolve', value: selectedConv.minutesToResolve !== undefined ? `${selectedConv.minutesToResolve} min` : '—' },
                    ].map(row => (
                      <Box key={row.label} sx={{ mb: 1.25 }}>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 13, wordBreak: 'break-word' }}>
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                    {selectedConv.callbackQuery && (
                      <Box sx={{ mb: 1.25 }}>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Callback Note
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>{selectedConv.callbackQuery}</Typography>
                      </Box>
                    )}
                    {selectedConv.resolutionNote && (
                      <Box>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Resolution Note
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>{selectedConv.resolutionNote}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Resolve Dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Resolve Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Optionally add a note about how this was resolved. The school will not see this note.
          </Typography>
          <TextField
            fullWidth label="Resolution note (optional)" value={resolveNote}
            onChange={e => setResolveNote(e.target.value)}
            size="small" multiline rows={3} autoFocus
            placeholder="e.g. Advised the school to re-register students through the portal."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<CheckCircleOutlineIcon />}
            onClick={submitResolve} disabled={resolving} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {resolving ? 'Resolving…' : 'Mark as Resolved'}
          </Button>
        </DialogActions>
      </Dialog>
    </Facilitator>
  );
};
